// 상품 카드 컴포넌트.
// 목록 화면(메인 베스트/카테고리 목록/검색 결과/찜 목록) 에서 공유한다.
// 작업계획서 17, 20: 사진/이름/가격(단위)/장바구니 담기 버튼, 품절 시 회색 + "품절" 라벨 + 장바구니 비활성.
//
// 손님 카피 톤([[feedback-customer-copy-tone]]): 부정/판단적 표현 회피.
// Phase 3-A 부터 우측 하단 "+" 버튼으로 즉시 담기 가능.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import type { CartItemOptionInput, ProductDto } from "@oo/shared";
import { CART_ITEM_QUANTITY_MAX, resolveUnitLabel } from "@oo/shared";
import { imageUrl } from "./imageUrl.ts";
import { useUnits } from "./useUnits.ts";
import ProductOptionsModal from "./ProductOptionsModal.tsx";
import { useCart } from "@/features/cart/useCart.ts";

interface Props {
  product: ProductDto;
}

// 상품 카드 컴포넌트 (담기/찜 포함).
export default function ProductCard({ product }: Props) {
  const url = imageUrl(product.mainImageKey);
  const units = useUnits();
  const unitLabel = resolveUnitLabel(units, product.unit, product.packWeightG);
  const cart = useCart();
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // "+" 를 누르면 바로 담지 않고 수량 조절 스테퍼를 연다. 담기를 눌러야 실제로 담는다.
  const [stepperOpen, setStepperOpen] = useState(false);
  const [qty, setQty] = useState(1);
  // 추가 옵션이 있는 상품은 스테퍼 대신 선택 모달을 띄운다(수량 + 옵션 개수 + 총 금액/중량).
  const hasOptions = product.options.length > 0;
  const [optionsOpen, setOptionsOpen] = useState(false);

  // 카드 자체가 Link 라, 카드 내 버튼은 클릭 시 상세 이동을 막고 자체 동작만 한다.
  function stop(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  // "+" 클릭: 옵션이 있으면 선택 모달, 없으면 기존 수량 스테퍼를 연다(수량 1로 시작).
  function openStepper(e: React.MouseEvent) {
    stop(e);
    if (product.isSoldOut) return;
    if (hasOptions) {
      setOptionsOpen(true);
      return;
    }
    setQty(1);
    setStepperOpen(true);
  }

  // 담기 결과를 토스트로 알린다(스테퍼/모달 공용).
  function showAddResult(result: Awaited<ReturnType<typeof cart.addItem>>) {
    if (result.ok) {
      setToast(result.capped ? "최대 수량까지 담았습니다." : "장바구니에 담았습니다.");
    } else if (result.reason === "distinct-limit") {
      setToast("장바구니에 담을 수 있는 상품 종류 한도를 초과했습니다.");
    } else if (result.reason === "sold-out") {
      setToast("이 상품은 지금 담을 수 없습니다.");
    } else {
      setToast("상품을 찾을 수 없습니다.");
    }
    window.setTimeout(() => setToast(null), 1500);
  }

  // 모달에서 담기 확정(기본 수량 + 옵션 개수).
  async function confirmAddWithOptions(quantity: number, options: CartItemOptionInput[]) {
    if (adding || product.isSoldOut) return;
    setAdding(true);
    try {
      const result = await cart.addItem(product.id, quantity, product, options);
      showAddResult(result);
      if (result.ok) {
        setOptionsOpen(false);
      }
    } finally {
      setAdding(false);
    }
  }

  // 스테퍼 수량 증감. 1 미만이면 스테퍼를 닫는다(취소), 최대는 카트 한도.
  function decQty(e: React.MouseEvent) {
    stop(e);
    setQty((q) => {
      if (q <= 1) {
        setStepperOpen(false);
        return 1;
      }
      return q - 1;
    });
  }
  function incQty(e: React.MouseEvent) {
    stop(e);
    setQty((q) => Math.min(CART_ITEM_QUANTITY_MAX, q + 1));
  }

  // 담기 클릭: 선택 수량만큼 장바구니에 담는다(낙관적 반영). 성공 시 스테퍼를 닫는다.
  async function confirmAdd(e: React.MouseEvent) {
    stop(e);
    if (adding || product.isSoldOut) return;
    setAdding(true);
    try {
      const result = await cart.addItem(product.id, qty, product);
      showAddResult(result);
      if (result.ok) {
        setStepperOpen(false);
        setQty(1);
      }
    } finally {
      setAdding(false);
    }
  }

  return (
    <>
    <Link
      to={`/product/${product.id}`}
      className="group relative flex flex-col overflow-hidden rounded-lg border border-ink-200 bg-surface shadow-card"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-ink-100">
        {url ? (
          <img
            src={url}
            alt={product.name}
            // 목록 카드 이미지는 화면 밖 분량이 많아 지연 로드로 초기 전송량을 줄인다.
            loading="lazy"
            decoding="async"
            className={`h-full w-full object-cover transition ${
              product.isSoldOut ? "opacity-50 grayscale" : "group-hover:scale-105"
            }`}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-ink-600">
            이미지 준비중
          </div>
        )}
        {product.isSoldOut ? (
          <span className="absolute left-2 top-2 rounded bg-ink-900/80 px-2 py-0.5 text-xs font-semibold text-white">
            품절
          </span>
        ) : null}
        {product.isBest ? (
          <span className="absolute right-2 top-2 rounded bg-brand-600 px-2 py-0.5 text-xs font-semibold text-white">
            베스트
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <h3 className={`line-clamp-1 text-sm font-medium ${product.isSoldOut ? "text-ink-400" : "text-ink-900"}`}>
          {product.name}
        </h3>
        {product.origin ? (
          <p className="text-xs text-ink-600">{product.origin}</p>
        ) : null}
        <div className="mt-auto flex items-end justify-between gap-2">
          {/* 가격/단위는 두 줄로 분리해 표시한다(작업지시).
              각 줄은 truncate 로 항상 한 줄을 유지해 좁은 2열 카드에서도 줄바꿈/넘침이 없다.
              스테퍼가 열리면 공간 확보를 위해 가격 줄을 숨긴다. */}
          {!stepperOpen ? (
            <div className="min-w-0 flex-1">
              <p className={`truncate text-base font-bold tabular-nums ${product.isSoldOut ? "text-ink-400" : "text-ink-900"}`}>
                {product.priceKrw.toLocaleString()}원
              </p>
              {unitLabel ? <p className="truncate text-xs text-ink-600">/ {unitLabel}</p> : null}
            </div>
          ) : null}

          {!stepperOpen ? (
            // 닫힘: "+" 버튼. 누르면 담지 않고 수량 스테퍼를 연다. 품절이면 비활성.
            // 터치 영역 44px 확보(WCAG 2.5.5): 시각 원은 36px 이되 패딩으로 히트영역을 넓힌다.
            <button
              type="button"
              onClick={openStepper}
              disabled={product.isSoldOut || adding}
              aria-label="수량 선택"
              className="group flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-full"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-white transition-colors duration-fast group-hover:bg-brand-700 group-disabled:bg-ink-300">
                <Plus size={20} aria-hidden="true" />
              </span>
            </button>
          ) : (
            // 열림: 수량 조절 스테퍼 + 담기. 담기를 눌러야 실제로 장바구니에 담긴다.
            <div className="flex flex-1 items-center gap-1.5">
              <div className="flex items-center rounded-full border border-brand-600 text-brand-700">
                <button
                  type="button"
                  onClick={decQty}
                  aria-label="수량 줄이기"
                  className="flex h-9 w-8 items-center justify-center"
                >
                  <Minus size={16} aria-hidden="true" />
                </button>
                <span className="min-w-6 text-center text-sm font-semibold tabular-nums">{qty}</span>
                <button
                  type="button"
                  onClick={incQty}
                  aria-label="수량 늘리기"
                  disabled={qty >= CART_ITEM_QUANTITY_MAX}
                  className="flex h-9 w-8 items-center justify-center disabled:opacity-40"
                >
                  <Plus size={16} aria-hidden="true" />
                </button>
              </div>
              <button
                type="button"
                onClick={confirmAdd}
                disabled={adding}
                className="flex h-9 flex-1 items-center justify-center whitespace-nowrap rounded-full bg-brand-600 px-2 text-xs font-semibold text-white transition-colors duration-fast hover:bg-brand-700 disabled:bg-ink-300"
              >
                담기
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 카드 단위 토스트. 짧게 나타났다 사라진다. role=status 로 스크린리더에도 알림. */}
      {toast ? (
        <span
          role="status"
          className="pointer-events-none absolute inset-x-2 bottom-2 rounded bg-ink-900/90 px-2 py-1 text-center text-[11px] text-white"
        >
          {toast}
        </span>
      ) : null}
    </Link>

    {/* 추가 옵션 선택 모달. 카드 Link 바깥에 두어 모달 클릭이 상세 이동으로 번지지 않게 한다. */}
    {optionsOpen ? (
      <ProductOptionsModal
        product={product}
        busy={adding}
        onClose={() => setOptionsOpen(false)}
        onConfirm={confirmAddWithOptions}
      />
    ) : null}
    </>
  );
}
